import { OverlayContainer } from '@angular/cdk/overlay';
import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DialogPosition, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { hasAnonymization } from '@collections/users/users.selectors';
import { AccessLevel } from '@core/shared-resources/shared-resources.model';
import { getCurrentSharedResourceTeamMembers } from '@core/shared-resources/store/shared-resources.selectors';
import { NoteService } from '@modules/notes/services/note.service';
import { notesCloseModal, notesCreate } from '@modules/notes/store';
import { Actions } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  AttachmentAdapter,
  AttachmentService,
  getVisibleToTeamMembersText,
  TeamMember,
} from '@shared';
import { DangerAlert } from '@shared/alert';
import {
  deleteAttachment,
  getFileNameWithoutExtension,
  uploadFiles,
  getExtensionFromFileName,
} from 'mojo-irp-attachments';
import { ModalReset, ModalShow } from '@shared/modal/modal.actions';
import { isModalConfirmed } from '@shared/modal/modal.reducer';
import { TeamMembersModalService } from '@shared/modal/team-members/team-members-modal.service';
import { TalentPoolsService } from '@shared/services/talent-pools.service';
import {
  Note,
  NoteCategories,
  NoteSubject,
  PersistedNote,
  mojoNotesUpdate,
  mojoNotesPreArchive,
} from 'mojo-irp-notes';
import { BehaviorSubject, Subscription } from 'rxjs';
import { first, tap } from 'rxjs/operators';
import { MojoEditorComponent } from 'mojo-editor';

export interface NotesModalComponentData {
  authorId: number;
  author: string;
  subject: NoteSubject;
  subjectId: number;
  isEditMode: boolean;
  note?: PersistedNote;
  closeNotes?: BehaviorSubject<boolean>;
}

@Component({
  selector: 'mojo-notes-modal',
  templateUrl: './notes-modal.component.html',
  styleUrls: ['./notes-modal.component.scss'],
})
export class NotesModalComponent implements OnInit, OnDestroy {
  @ViewChild(MojoEditorComponent) editor: MojoEditorComponent;

  private readonly publicNote = 'public';
  readonly sharedNote = 'shared';
  readonly privateNote = 'private';

  hasAnonymization$ = this.store.select(hasAnonymization);
  currentTeamMembers: TeamMember[];
  teamMembersForMentions: TeamMember[] = [];
  visibilityOptions: { value: string; label: string }[] = [
    { value: this.publicNote, label: 'Public' },
    { value: this.sharedNote, label: 'Me and others' },
    { value: this.privateNote, label: 'Me only' },
  ];
  tooltip$ = this.modalService.notesModalOptions$;
  editTooltip = getVisibleToTeamMembersText(this.data.note?.teamMembers);
  subs = new Subscription();

  message: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
    Validators.minLength(2),
    Validators.maxLength(1000),
  ]);

  disableTitle = !!this.data?.isEditMode && !!this.data?.note?.systemGenerated;
  title = new UntypedFormControl(
    { value: '', disabled: this.disableTitle },
    Validators.maxLength(250),
  );

  noteVisibility = new UntypedFormControl();

  form = new UntypedFormGroup({
    title: this.title,
    message: this.message,
    noteVisibility: this.noteVisibility,
  });

  increaseDialogSize = false;

  backdropElement = this.overlayContainer
    .getContainerElement()
    .querySelector('.cdk-global-overlay-wrapper');

  files: File[] = [];

  attachments: Partial<AttachmentAdapter>[] = !!this.data?.note?.attachments
    ? this.data?.note?.attachments
    : [];

  attachmentsToRemove: Partial<AttachmentAdapter>[] = [];

  allowedNumberOfFiles = 5;

  shouldCloseDialogOnDiscard = false;

  alertCallback = (msg) => this.store.dispatch(new DangerAlert(msg));

  get showMentionTags() {
    return (
      this.data.subject === NoteSubject.Job ||
      this.data.subject === NoteSubject.JobHiringManager ||
      this.data.subject === NoteSubject.Candidate ||
      this.data.subject === NoteSubject.TalentPool
    );
  }

  constructor(
    private dialogRef: MatDialogRef<NotesModalComponent>,
    private store: Store,
    @Inject(MAT_DIALOG_DATA) public data: NotesModalComponentData,
    private router: Router,
    private overlayContainer: OverlayContainer,
    public talentPoolsService: TalentPoolsService,
    private updates$: Actions,
    private noteService: NoteService,
    private attachmentService: AttachmentService,
    private modalService: TeamMembersModalService,
  ) {}

  ngOnInit() {
    this.noteVisibility.setValue(this.visibilityOptions[0].value);
    this.subs.add(
      this.noteVisibility.valueChanges.subscribe((changedValue) => {
        if (changedValue !== this.sharedNote) {
          this.teamMembersForMentions = [];
        } else {
          this.teamMembersForMentions = this.currentTeamMembers;
        }
      }),
    );
    this.getCurrentTeamMembersFromStore();
    if (this.data.isEditMode && !!this.data.note) {
      this.title.setValue(this.getTitleWithoutModal(this.data.note?.title));
      this.message.setValue(this.data.note?.content);
      this.setVisibilityOption();
    }
    this.deleteOnConfirm();
    this.closeOnRedirect();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.store.dispatch(new ModalReset());
  }

  //main actions

  submit() {
    this.data.isEditMode && !!this.data.note ? this.update() : this.save();
    this.closeDialog(true);
  }

  private save(noteCategory: NoteCategories = NoteCategories.NoCategory) {
    const note: Note = {
      subjectId: this.data.subjectId,
      subjectType: this.data.subject,
      category: noteCategory,
      content: this.message.value,
      title: this.title?.value,
      attachments: this.getAttachments(),
      files: this.files,
      isPublic: this.noteVisibility.value === this.publicNote,
      teamMembers: this.getSelectedTeamMembers(),
    };
    this.store.dispatch(notesCreate({ payload: note }));
  }

  private update(noteCategory: NoteCategories = NoteCategories.NoCategory) {
    const note: PersistedNote = {
      id: this.data.note?.id,
      subjectId: this.data.note.subjectId,
      subjectType: this.data.note.subjectType,
      systemGenerated: this.data.note.systemGenerated,
      category: noteCategory,
      content: this.message.value,
      title: this.getTitleWithModal(this.title?.value),
      attachments: this.getAttachments(),
      files: this.files,
      isPublic: this.noteVisibility.value === this.publicNote,
      teamMembers: this.getSelectedTeamMembers(),
    };
    this.store.dispatch(
      mojoNotesUpdate({ payload: note, attachmentsToRemove: this.attachmentsToRemove }),
    );
  }

  @HostListener('window:beforeunload')
  saveAsDraft() {
    if (this.message.value !== '' && this.messageChanged()) {
      this.data.isEditMode && !!this.data.note
        ? this.update(NoteCategories.Draft)
        : this.save(NoteCategories.Draft);
    }
    this.closeDialog(true);
  }

  discardNote() {
    this.store.dispatch(
      new ModalShow({
        title: 'Are you sure you want to discard this note?',
        message: 'The note will be deleted.',
      }),
    );
    this.shouldCloseDialogOnDiscard = true;
  }

  //modal actions

  private deleteOnConfirm() {
    this.subs.add(
      this.store
        .pipe(
          select(isModalConfirmed),
          first(Boolean),
          tap(() => {
            this.deleteAndClose();
          }),
        )
        .subscribe(),
    );
  }

  private closeOnRedirect() {
    const previousUrl = this.router.url;
    this.subs.add(
      this.router.events.subscribe((val) => {
        if (val instanceof NavigationEnd) {
          const newRouteComponent = val.url.split('/');
          const previousRouteComponent = previousUrl.split('/');
          if (
            newRouteComponent.length <= 2 ||
            (newRouteComponent[1] !== previousRouteComponent[1] &&
              newRouteComponent[2] !== previousRouteComponent[2])
          ) {
            this.saveAsDraft();
          }
        }
      }),
    );
    this.subs.add(
      this.data.closeNotes?.subscribe((close) => {
        if (!!close) {
          this.saveAsDraft();
        }
      }),
    );
  }

  private deleteAndClose() {
    if (!!this.data.note) {
      this.store.dispatch(mojoNotesPreArchive({ note: this.data.note }));
    }
    if (this.shouldCloseDialogOnDiscard) {
      this.closeDialog(true);
      this.shouldCloseDialogOnDiscard = false;
    }
  }

  closeDialog(actionTriggered: boolean = false) {
    this.form.reset();
    this.editor?.reset();
    this.dialogRef.close(actionTriggered);
    this.store.dispatch(new ModalReset());
    this.store.dispatch(notesCloseModal());
  }

  expandDialog() {
    this.increaseDialogSize = !this.increaseDialogSize;
    if (this.increaseDialogSize) {
      this.makeDialogLarge();
    } else {
      this.makeDialogSmall();
    }
  }

  private makeDialogLarge() {
    this.dialogRef.removePanelClass('medium-size-modal');
    this.dialogRef.addPanelClass('large-size-modal');
    const dialogPosition: DialogPosition = {
      top: '',
      bottom: '',
      left: '',
      right: '',
    };
    this.dialogRef.updatePosition(dialogPosition);
    this.dialogRef.updateSize('59vw', 'unset');
    this.backdropElement.classList.add('cdk-overlay-backdrop-added');
  }

  private makeDialogSmall() {
    this.dialogRef.removePanelClass('large-size-modal');
    this.dialogRef.addPanelClass('medium-size-modal');
    const dialogPosition: DialogPosition = {
      bottom: '0',
      right: '17rem',
    };
    this.dialogRef.updatePosition(dialogPosition);
    this.dialogRef.updateSize('35rem', 'unset');
    this.backdropElement.classList.remove('cdk-overlay-backdrop-added');
  }

  openTeamMembersModal(visibility: string) {
    if (visibility === this.sharedNote) {
      this.modalService.openNotesModal();
    }
  }

  //files

  deleteAttachment(attachmentToDelete) {
    [this.files, this.attachments, this.attachmentsToRemove] = deleteAttachment(
      this.files,
      this.attachments,
      this.attachmentsToRemove,
      attachmentToDelete,
    );
  }

  private getAttachments() {
    const attachments = [];
    this.attachments.forEach((attachment) =>
      attachments.push({ id: attachment.id, name: attachment.name }),
    );
    return attachments;
  }

  uploadFiles($event: File[]) {
    [this.files, this.attachments] = uploadFiles(
      this.files,
      this.attachments,
      $event,
      this.alertCallback,
      this.allowedNumberOfFiles,
    );
  }

  //team members

  private getCurrentTeamMembersFromStore() {
    this.subs.add(
      this.store
        .select(getCurrentSharedResourceTeamMembers)
        .subscribe((current) => (this.currentTeamMembers = current)),
    );
  }

  private getSelectedTeamMembers(): TeamMember[] {
    switch (this.noteVisibility.value) {
      case this.publicNote:
        return [];
      case this.privateNote:
        return this.data.authorId
          ? [{ id: this.data.authorId, accessLevel: AccessLevel.owner }]
          : [];
      case this.sharedNote: {
        this.currentTeamMembers.forEach(
          (teamMember) => (teamMember.accessLevel = AccessLevel.read),
        );
        return [
          { id: this.data.authorId, accessLevel: AccessLevel.owner },
          ...this.currentTeamMembers,
        ];
      }
    }
  }

  private visibilityChanged(): boolean {
    if (this.noteVisibility.value === this.publicNote && this.data.note?.isPublic) {
      return false;
    }
    if (
      this.noteVisibility.value === this.privateNote &&
      this.data.note?.teamMembers?.length === 0
    ) {
      return false;
    }
    if (this.noteVisibility.value === this.sharedNote && this.data.note?.teamMembers?.length >= 1) {
      return false;
    }
    return true;
  }

  private setVisibilityOption() {
    if (this.data.note.isPublic) {
      this.noteVisibility.setValue(this.visibilityOptions[0].value);
      return;
    }
    if (this.data.note?.teamMembers.length >= 1) {
      this.noteVisibility.setValue(this.visibilityOptions[1].value);
    } else {
      this.noteVisibility.setValue(this.visibilityOptions[2].value);
    }
  }

  //helpers

  getFileNameWithoutExtension(name: string): string {
    return getFileNameWithoutExtension(name);
  }

  getExtension(name: string): string {
    return getExtensionFromFileName(name);
  }

  private getTitleWithModal(title: string): string {
    if (this.data.note?.title?.includes('modal:')) {
      const modal = this.data.note?.title?.split('|')[0];
      return `${modal}|${title}`;
    }
    return title;
  }

  private getTitleWithoutModal(title: string): string {
    if (title?.includes('modal:')) {
      return this.data.note?.title?.split('|')[1];
    }
    return title;
  }

  private messageChanged(): boolean {
    return (
      this.message.value !== this.data.note?.content ||
      this.title.value !== this.data.note?.title ||
      this.visibilityChanged()
    );
  }
}
